import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { useChatStore } from './chatStore';

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    accessToken: string | null;
    refreshToken: string | null;
    tokenExpiry: number | null;
    /** 登出原因（会话过期/闲置超时等），用于登录页提示；不持久化 */
    logoutReason: string | null;
    login: (userData: User, tokens: { access_token: string; refresh_token: string; expires_in: number }) => void;
    logout: (reason?: string) => void;
    clearLogoutReason: () => void;
    updateTokens: (tokens: { access_token: string; refresh_token?: string; expires_in: number }) => void;
    isTokenExpired: () => boolean;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            logoutReason: null,

            login: (userData: User, tokens: { access_token: string; refresh_token: string; expires_in: number }) => {
                const expiryTime = Date.now() + (tokens.expires_in * 1000);
                set({
                    user: userData,
                    isAuthenticated: true,
                    accessToken: tokens.access_token,
                    refreshToken: tokens.refresh_token,
                    tokenExpiry: expiryTime,
                    logoutReason: null,
                });
            },

            logout: (reason?: string) => {
                // 登出时清空聊天记录，避免下一位使用者看到上一份会话内容
                useChatStore.getState().clearChat();
                set({
                    user: null,
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    logoutReason: reason ?? null,
                });
            },

            clearLogoutReason: () => set({ logoutReason: null }),

            updateTokens: (tokens: { access_token: string; refresh_token?: string; expires_in: number }) => {
                const expiryTime = Date.now() + (tokens.expires_in * 1000);
                set(state => ({
                    accessToken: tokens.access_token,
                    refreshToken: tokens.refresh_token || state.refreshToken,
                    tokenExpiry: expiryTime
                }));
            },

            isTokenExpired: () => {
                const { tokenExpiry } = get();
                if (!tokenExpiry) return true;
                // 提前5分钟刷新token
                return Date.now() > (tokenExpiry - 5 * 60 * 1000);
            },
        }),
        {
            name: 'ntrk-auth',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                tokenExpiry: state.tokenExpiry,
            }),
        }
    )
);